import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import {
    Search,
    Package,
    RefreshCw,
    CheckCircle,
    X,
    Phone,
    MapPin,
    User,
    Trash2,
    AlertTriangle
} from 'lucide-react';
import api from '../../services/api';
import orderService from '../../services/orderService';
import Toast from '../common/Toast';

const AdminOrders = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [toast, setToast] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        const initData = async () => {
            setIsLoading(true);
            try {
                // Fetch products first to map IDs to names
                const productRes = await api.get('/api/products/');
                const productMap = {};
                productRes.data.forEach(p => {
                    productMap[p.id] = p.name;
                });
                setProducts(productMap);

                await loadOrders();
            } catch (error) {
                console.error('Error initializing admin orders:', error);
            } finally {
                setIsLoading(false);
            }
        };
        initData();
    }, []);

    const loadOrders = async () => {
        try {
            const data = await orderService.getAllOrders();
            // Trier les commandes par date de création décroissante (plus récentes d'abord)
            const sortedData = data.sort((a, b) => {
                return new Date(b.created_at) - new Date(a.created_at);
            });
            setOrders(sortedData);
        } catch (error) {
            console.error('Error loading orders:', error);
            setToast({ message: 'Erreur lors du chargement des commandes', type: 'error' });
        }
    };

    const handleUpdateStatus = async (orderId, newStatus) => {
        try {
            await orderService.updateOrder(orderId, { status: newStatus });
            setToast({ message: 'Statut de la commande mis à jour', type: 'success' });
            loadOrders();
            if (selectedOrder?.id === orderId) {
                setSelectedOrder(prev => ({ ...prev, status: newStatus }));
            }
        } catch (error) {
            console.error('Error updating status:', error);
            setToast({ message: 'Erreur lors de la mise à jour du statut', type: 'error' });
        }
    };

    const handleDeleteOrder = async (orderId) => {
        try {
            await orderService.deleteOrder(orderId);
            setToast({ message: 'Commande supprimée avec succès', type: 'success' });
            setSelectedOrder(null);
            setShowDeleteConfirm(false);
            loadOrders();
        } catch (error) {
            console.error('Error deleting order:', error);
            setToast({ message: 'Erreur lors de la suppression de la commande', type: 'error' });
        }
    };

    const handleUpdatePaymentStatus = async (orderId, isPaid, currentStatus) => {
        try {
            const updates = { is_paid: isPaid };

            // Logic: Auto-transition to Processing if payment is confirmed for a Pending order
            let statusChanged = false;
            if (isPaid && currentStatus === 'Pending') {
                updates.status = 'Processing';
                statusChanged = true;
            }

            await orderService.updateOrder(orderId, updates);

            const message = statusChanged
                ? `Paiement confirmé ! La commande est passée en "Préparation"`
                : `Paiement marqué comme ${isPaid ? 'effectué' : 'non réglé'}`;

            setToast({ message, type: 'success' });
            loadOrders();
            if (selectedOrder?.id === orderId) {
                setSelectedOrder(prev => ({ ...prev, ...updates }));
            }
        } catch (error) {
            console.error('Error updating payment status:', error);
            setToast({ message: 'Erreur lors de la mise à jour du paiement', type: 'error' });
        }
    };

    const getStatusStyle = (status) => {
        const s = status?.toLowerCase();
        switch (s) {
            case 'pending': return 'bg-amber-50 text-amber-600 border-amber-100';
            case 'processing': return 'bg-sky-50 text-sky-600 border-sky-100';
            case 'shipped': return 'bg-indigo-50 text-indigo-600 border-indigo-100';
            case 'delivered': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
            case 'cancelled': return 'bg-red-50 text-red-600 border-red-100';
            default: return 'bg-slate-50 text-slate-600 border-slate-100';
        }
    };

    return (
        <div className="space-y-10">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-black text-green-950 mb-2">Gestion des Commandes</h1>
                    <p className="text-slate-500">Suivez et gérez les commandes de vos clients en temps réel.</p>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant={showAll ? "secondary" : "primary"}
                        onClick={() => setShowAll(!showAll)}
                        className="h-10 px-4 text-sm"
                    >
                        {showAll ? 'Voir moins' : 'Voir tout'}
                    </Button>
                    <Button variant="primary" onClick={loadOrders} className="h-10 px-4 text-sm">
                        Rafraîchir
                    </Button>
                </div>
            </div>

            {/* Main Content: Split View */}
            <div className="flex flex-col lg:flex-row gap-8">
                {/* Left Side: Orders List */}
                <div className={`${selectedOrder ? 'lg:w-[60%]' : 'w-full'} space-y-6 transition-all duration-500`}>
                    {/* Filters Bar */}
                    <div className="bg-white p-4 rounded-[2rem] border border-slate-100 shadow-sm flex items-center gap-4">
                        <div className="relative flex-grow">
                            <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                            <input
                                type="text"
                                placeholder="Rechercher par ID ou nom..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full h-14 pl-14 pr-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand transition-all outline-none font-medium text-sm"
                            />
                        </div>
                    </div>

                    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            {isLoading ? (
                                <div className="flex flex-col items-center justify-center py-20">
                                    <RefreshCw className="w-10 h-10 text-brand animate-spin mb-4" />
                                    <p className="text-slate-500 font-bold">Chargement...</p>
                                </div>
                            ) : (
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-slate-50/50 text-left">
                                            <th className="px-6 py-6 text-[10px] font-black text-slate-400 border-b border-slate-100 uppercase tracking-widest">Client / WhatsApp</th>
                                            <th className="px-6 py-6 text-[10px] font-black text-slate-400 border-b border-slate-100 uppercase tracking-widest ">Produits</th>
                                            <th className="px-6 py-6 text-[10px] font-black text-slate-400 border-b border-slate-100 uppercase tracking-widest text-center">Status</th>
                                            <th className="px-6 py-6 text-[10px] font-black text-slate-400 border-b border-slate-100 uppercase tracking-widest text-right">Montant</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {orders.filter(order => {
                                            const searchLower = searchTerm.toLowerCase();
                                            return order.id?.toString().includes(searchLower) ||
                                                order.full_name?.toLowerCase().includes(searchLower);
                                        }).slice(0, showAll ? undefined : 3).map((order) => (
                                            <tr
                                                key={order.id}
                                                onClick={() => setSelectedOrder(order)}
                                                className={`cursor-pointer transition-all hover:bg-slate-50/50 group ${selectedOrder?.id === order.id ? 'bg-brand/5' : ''}`}
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${selectedOrder?.id === order.id ? 'bg-brand text-white' : 'bg-brand/5 text-brand'}`}>
                                                            {order.payment_method === 'WhatsApp' ? (
                                                                <Phone className="w-4 h-4" />
                                                            ) : (
                                                                <Package className="w-4 h-4" />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-extrabold text-green-950 text-xs truncate max-w-[120px]">{order.full_name}</p>
                                                            <p className="text-[9px] text-slate-400 font-bold">
                                                                #{order.id} • {new Date(order.created_at).toLocaleDateString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 ">
                                                    <div className="max-w-[150px]">
                                                        <p className="text-[10px] font-bold text-slate-600 truncate">
                                                            {order.items?.map(item => products[item.product] || 'Article').join(', ') || 'Aucun produit'}
                                                        </p>
                                                        <p className="text-[9px] text-brand font-black uppercase tracking-tighter">
                                                            {order.payment_method}
                                                        </p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter border ${getStatusStyle(order.status)} text-center mx-auto w-fit`}>
                                                        {order.status}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <p className="font-extrabold text-green-700 text-xs">{Math.floor(order.total_price).toLocaleString('fr-FR')} F</p>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Side: Detailed View */}
                {selectedOrder && (
                    <div className="lg:w-[40%] animate-in slide-in-from-right duration-500">
                        <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl p-8 sticky top-28 space-y-8">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-xl font-black text-green-950 flex items-center gap-2">
                                        <div className="w-2 h-6 bg-brand rounded-full"></div>
                                        Détails
                                    </h2>
                                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">#{selectedOrder.id} • {selectedOrder.payment_method}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-all"
                                        title="Supprimer la commande"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedOrder(null);
                                            setShowDeleteConfirm(false);
                                        }}
                                        className="w-10 h-10 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-slate-100 hover:text-slate-600 transition-all"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Delete Confirmation Dialog */}
                            {showDeleteConfirm && (
                                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex flex-col gap-3 animate-in fade-in zoom-in-95 duration-200">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-red-500 mx-auto mb-2">
                                        <AlertTriangle className="w-6 h-6" />
                                    </div>
                                    <div className="text-center mb-4">
                                        <h3 className="text-red-800 font-bold mb-1">Supprimer la commande ?</h3>
                                        <p className="text-red-600 text-xs">Cette action est irréversible. Toutes les données associées à cette commande seront perdues.</p>
                                    </div>
                                    <div className="flex gap-3">
                                        <Button
                                            variant="secondary"
                                            className="flex-1 bg-white border-slate-200 text-slate-700 hover:bg-slate-50 text-xs py-2"
                                            onClick={() => setShowDeleteConfirm(false)}
                                        >
                                            Annuler
                                        </Button>
                                        <Button
                                            variant="primary"
                                            className="flex-1 bg-red-500 hover:bg-red-600 text-white border-transparent text-xs py-2"
                                            onClick={() => handleDeleteOrder(selectedOrder.id)}
                                        >
                                            Confirmer
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Status Controls */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Etat Livraison</label>
                                    <select
                                        value={selectedOrder.status}
                                        onChange={(e) => handleUpdateStatus(selectedOrder.id, e.target.value)}
                                        className={`w-full px-4 py-3 rounded-xl border text-[10px] font-black uppercase tracking-widest outline-none transition-all ${getStatusStyle(selectedOrder.status)}`}
                                    >
                                        <option value="Pending">En attente</option>
                                        <option value="Processing">Préparation</option>
                                        <option value="Shipped">Expédié</option>
                                        <option value="Delivered">Livré</option>
                                        <option value="Cancelled">Annulé</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Statut Paiement</label>
                                    <button
                                        onClick={() => handleUpdatePaymentStatus(selectedOrder.id, !selectedOrder.is_paid, selectedOrder.status)}
                                        className={`w-full h-11 px-4 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all shadow-sm flex items-center justify-center gap-2 ${selectedOrder.is_paid
                                            ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                            : 'bg-brand text-white border-brand hover:bg-brand-dark'}`}
                                    >
                                        {selectedOrder.is_paid && <CheckCircle className="w-4 h-4" />}
                                        {selectedOrder.is_paid ? 'Payé' : 'Confirmer le Règlement'}
                                    </button>
                                </div>
                            </div>

                            {/* Client Info */}
                            <div className="p-5 bg-slate-50 rounded-2xl space-y-4">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-brand shadow-sm border border-slate-100">
                                        <User className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">Client / Email</p>
                                        <p className="font-bold text-green-950 text-sm">{selectedOrder.full_name}</p>
                                        <p className="text-xs text-slate-500">{selectedOrder.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-brand shadow-sm border border-slate-100">
                                        <Phone className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">Contact</p>
                                        <p className="font-bold text-green-950 text-sm">{selectedOrder.phone}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-brand shadow-sm border border-slate-100">
                                        <MapPin className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">Lieu de Livraison</p>
                                        <p className="font-bold text-green-950 text-xs leading-tight">{selectedOrder.address}, {selectedOrder.city}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Items Breakdown */}
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-4 flex justify-between items-center">
                                    Articles
                                    <span className="text-brand font-black">{selectedOrder.items?.length || 0}</span>
                                </p>
                                <div className="space-y-3 max-h-[160px] overflow-y-auto pr-2 custom-scrollbar">
                                    {selectedOrder.items?.map((item, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-slate-50/50 border border-slate-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-[10px] font-black text-brand shadow-sm italic">
                                                    {item.quantity}
                                                </div>
                                                <p className="font-bold text-green-950 text-[11px] truncate max-w-[140px]">
                                                    {products[item.product] || `Article #${item.product}`}
                                                </p>
                                            </div>
                                            <p className="font-black text-brand text-[10px]">
                                                {Math.floor(item.price * item.quantity).toLocaleString('fr-FR')} F
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Total Bar */}
                            <div className="pt-6 border-t border-slate-100 flex items-center justify-between">
                                <div>
                                    <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">Montant Total</p>
                                    <p className="text-2xl font-black text-green-950">{Math.floor(selectedOrder.total_price).toLocaleString('fr-FR')} <span className="text-brand text-sm italic uppercase">Fcfa</span></p>
                                </div>
                                <div className={`px-4 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest ${selectedOrder.is_paid ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-500 border-red-100'}`}>
                                    {selectedOrder.is_paid ? 'Validé' : 'À Percevoir'}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default AdminOrders;
